import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiAlertOutline, mdiShapeOutline } from '@mdi/js'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { AppIcon } from '../ui/icon'
import { EditableValue } from '../ui/editable-value'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from '../ui/select'
import { useLocale } from '../../contexts/LocaleContext'
import { useUpdateExpense } from '../../hooks/useQueries'
import { pickLabel, useExpenseCategories, useExpenseClassification } from '../../hooks/useExpenseStatement'

const AUTOMATIC = '__automatic__'

/**
 * The expense category of one line (spec 2026-10-22-01): what the ladder
 * deduces and from what, or what a human pinned. Choosing "Automatic" hands
 * the line back to the ladder.
 */
export function ExpenseCategoryCard({ org, expenseUid, canEdit }: { org: string; expenseUid: string; canEdit: boolean }) {
  const { t } = useTranslation('expenses')
  const { locale } = useLocale()
  const { data: taxonomy } = useExpenseCategories(org)
  const { data: classification, refetch } = useExpenseClassification(org, expenseUid)
  const update = useUpdateExpense(org, expenseUid)
  const [editing, setEditing] = useState(false)

  const categoryLabel = (code: string) => {
    if (code === 'unclassified') return t('statement.row_unclassified')
    const c = taxonomy?.categories.find((x) => x.code === code)
    return c ? pickLabel(c.label, locale) : code
  }

  const choose = async (value: string) => {
    setEditing(false)
    await update.mutateAsync({ cost_category: value === AUTOMATIC ? '' : value })
    refetch()
  }

  const unclassified = classification?.category === 'unclassified'

  return (
    <Card data-testid="expense-category-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <AppIcon path={mdiShapeOutline} size="md" />
          {t('category.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {editing ? (
          <Select
            defaultOpen
            value={classification?.source === 'manual' ? classification.category : AUTOMATIC}
            onValueChange={choose}
            onOpenChange={(open) => !open && setEditing(false)}
          >
            <SelectTrigger data-testid="expense-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={AUTOMATIC} data-testid="expense-category-option-automatic">
                {t('category.automatic')} — {t('category.automatic_hint')}
              </SelectItem>
              <SelectSeparator />
              {taxonomy?.families.map((f) => (
                <SelectGroup key={f.code}>
                  <SelectLabel>{pickLabel(f.label, locale)}</SelectLabel>
                  {taxonomy.categories.filter((c) => c.family === f.code).map((c) => (
                    <SelectItem key={c.code} value={c.code} data-testid={`expense-category-option-${c.code}`}>
                      {pickLabel(c.label, locale)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <EditableValue
            canEdit={canEdit}
            onEdit={() => setEditing(true)}
            title={t('category.edit_title')}
            placeholder={t('statement.row_unclassified')}
            className={unclassified ? 'font-medium text-amber-700 dark:text-amber-400' : 'font-medium text-foreground'}
            data-testid="expense-category-value"
          >
            {classification ? categoryLabel(classification.category) : ''}
          </EditableValue>
        )}

        {classification && (
          <div className="flex flex-wrap items-center gap-1" data-testid="expense-category-meta">
            {!unclassified && <Badge variant="outline" className="text-[11px] font-normal" data-testid="expense-category-source">
              {t(`category.source_${classification.source}`)}
            </Badge>}
            <Badge variant={classification.regime === 'works' ? 'secondary' : 'outline'} className="text-[11px] font-normal">
              {t(`category.regime_${classification.regime}`)}
              {classification.under_contract && ` · ${t('category.under_contract')}`}
            </Badge>
          </div>
        )}

        {classification && !unclassified && (
          <p className="text-sm text-muted-foreground" data-testid="expense-category-account">
            <span>{t('category.account')}</span>{' '}
            {classification.account
              ? <span className="text-foreground">{classification.account} · {pickLabel(classification.account_label, locale)}{classification.jurisdiction ? ` (${classification.jurisdiction})` : ''}</span>
              : t('category.no_account')}
          </p>
        )}

        {unclassified && (
          <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400" data-testid="expense-category-hint">
            <AppIcon path={mdiAlertOutline} size="xs" className="mt-0.5 shrink-0" />
            {t('category.unclassified_hint')}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
